import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Music, Mic2, Radio, Disc3, Mail, Calendar, MapPin, Headphones } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import MediaKitNav from "@/components/MediaKitNav";
import AnimatedCounter from "@/components/AnimatedCounter";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function MediaKitMusicaPage() {
  const [stats, setStats] = useState(null);
  const [musicStats, setMusicStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, musicRes] = await Promise.all([
        axios.get(`${API}/media-kit/stats`),
        axios.get(`${API}/media-kit/music`)
      ]);
      setStats(statsRes.data);
      setMusicStats(musicRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <img 
          src="https://customer-assets-0z36b82j.emergentagent.net/job_6374765b-0927-4fa7-ace6-9a95eb25e587/artifacts/6cbu7wu9_LOADING.gif" 
          alt="Cargando..."
          className="w-48 h-auto"
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col text-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#9146FF]/20 via-transparent to-transparent pointer-events-none" />
        
        <div className="max-w-6xl mx-auto relative z-10">
          {/* Logo */}
          <div className="text-center mb-8">
            <h1 
              className="text-5xl md:text-7xl font-bold mb-4"
              style={{ 
                fontFamily: "'Supersonic Rocketship', Impact, sans-serif",
                color: "#FFE500"
              }}
            >
              MÚSICA
            </h1>
            <p className="text-xl md:text-2xl text-gray-300">
              Presentaciones en vivo y sync licensing
            </p>
          </div>

          {/* Navigation */}
          <MediaKitNav />

          {/* Music Stats */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6 mb-16">
            {/* Spotify */}
            <div 
              className="bg-gradient-to-br from-green-900/40 to-green-950/40 border border-green-500/30 rounded-2xl p-6 text-center hover:scale-105 transition-transform duration-300"
              data-testid="stat-spotify"
            >
              <Headphones className="w-10 h-10 mx-auto mb-3 text-green-500" />
              <div className="text-3xl md:text-4xl font-bold text-white mb-1">
                <AnimatedCounter value={musicStats?.spotify?.monthly_listeners || 50000} />
              </div>
              <p className="text-gray-400 text-sm">Oyentes mensuales</p>
            </div>

            {/* YouTube Music */}
            <div 
              className="bg-gradient-to-br from-red-900/40 to-red-950/40 border border-red-500/30 rounded-2xl p-6 text-center hover:scale-105 transition-transform duration-300"
              data-testid="stat-youtube-music"
            >
              <Music className="w-10 h-10 mx-auto mb-3 text-red-500" />
              <div className="text-3xl md:text-4xl font-bold text-white mb-1">
                <AnimatedCounter value={stats?.youtube?.subscribers || 0} />
              </div>
              <p className="text-gray-400 text-sm">Suscriptores YouTube</p>
            </div>

            {/* Original Songs */}
            <div 
              className="col-span-2 md:col-span-1 bg-gradient-to-br from-purple-900/40 to-purple-950/40 border border-purple-500/30 rounded-2xl p-6 text-center hover:scale-105 transition-transform duration-300"
              data-testid="stat-songs"
            >
              <Disc3 className="w-10 h-10 mx-auto mb-3 text-purple-500" />
              <div className="text-3xl md:text-4xl font-bold text-white mb-1">
                50+
              </div>
              <p className="text-gray-400 text-sm">Canciones originales</p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 px-4 bg-gradient-to-b from-transparent to-[#111]">
        <div className="max-w-6xl mx-auto">
          <h2 
            className="text-3xl md:text-4xl font-bold text-center mb-12"
            style={{ fontFamily: "'Supersonic Rocketship', Impact, sans-serif" }}
          >
            <span className="text-[#E91E8C]">NUESTROS</span>{" "}
            <span className="text-white">SERVICIOS</span>
          </h2>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Live Performances */}
            <div 
              className="bg-gradient-to-br from-[#E91E8C]/10 to-transparent border border-[#E91E8C]/30 rounded-3xl p-8"
              data-testid="service-live"
            >
              <Mic2 className="w-12 h-12 text-[#E91E8C] mb-6" />
              <h3 
                className="text-2xl font-bold mb-4 text-white"
                style={{ fontFamily: "'Supersonic Rocketship', Impact, sans-serif" }}
              >
                PRESENTACIONES EN VIVO
              </h3>
              <p className="text-gray-300 mb-6">
                Shows en vivo con los personajes de Vete a la Versh. 
                Ideal para eventos, convenciones, festivales y presentaciones corporativas.
              </p>
              
              <ul className="space-y-3">
                <li className="flex items-center gap-3 text-gray-300">
                  <Calendar className="w-5 h-5 text-[#FFE500]" />
                  <span>Eventos corporativos</span>
                </li>
                <li className="flex items-center gap-3 text-gray-300">
                  <MapPin className="w-5 h-5 text-[#FFE500]" />
                  <span>Convenciones y expos</span>
                </li>
                <li className="flex items-center gap-3 text-gray-300">
                  <Music className="w-5 h-5 text-[#FFE500]" />
                  <span>Festivales y conciertos</span>
                </li>
              </ul>
            </div>

            {/* Sync Licensing */}
            <div 
              className="bg-gradient-to-br from-[#FFE500]/10 to-transparent border border-[#FFE500]/30 rounded-3xl p-8"
              data-testid="service-sync"
            >
              <Radio className="w-12 h-12 text-[#FFE500] mb-6" />
              <h3 
                className="text-2xl font-bold mb-4 text-white"
                style={{ fontFamily: "'Supersonic Rocketship', Impact, sans-serif" }}
              >
                SYNC LICENSING
              </h3>
              <p className="text-gray-300 mb-6">
                Licencia nuestra música original para tus proyectos. 
                Canciones, intros, efectos de sonido y más.
              </p>
              
              <ul className="space-y-3">
                <li className="flex items-center gap-3 text-gray-300">
                  <Disc3 className="w-5 h-5 text-[#E91E8C]" />
                  <span>Spots publicitarios</span>
                </li>
                <li className="flex items-center gap-3 text-gray-300">
                  <Disc3 className="w-5 h-5 text-[#E91E8C]" />
                  <span>Videos corporativos</span>
                </li>
                <li className="flex items-center gap-3 text-gray-300">
                  <Disc3 className="w-5 h-5 text-[#E91E8C]" />
                  <span>Videojuegos y apps</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Portfolio Section */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 
            className="text-3xl md:text-4xl font-bold text-center mb-12"
            style={{ fontFamily: "'Supersonic Rocketship', Impact, sans-serif" }}
          >
            <span className="text-[#FFE500]">CANCIONES</span>{" "}
            <span className="text-white">DESTACADAS</span>
          </h2>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Song Cards */}
            {[
              { name: "Intro Vete a la Versh", type: "Opening", plays: "1M+" },
              { name: "Mecoboy's Theme", type: "Character Theme", plays: "500K+" },
              { name: "Darkar Rap", type: "Single", plays: "800K+" },
            ].map((song, index) => (
              <div 
                key={index}
                className="bg-gradient-to-br from-white/5 to-white/0 border border-white/10 rounded-2xl p-6 hover:border-[#E91E8C]/50 transition-all duration-300 hover:scale-105"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-[#E91E8C] to-[#FFE500] rounded-full flex items-center justify-center mb-4">
                  <Music className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-bold text-white mb-1">{song.name}</h3>
                <p className="text-gray-500 text-sm mb-2">{song.type}</p>
                <p className="text-[#E91E8C] font-bold">{song.plays} plays</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center bg-gradient-to-r from-[#9146FF]/20 via-transparent to-[#9146FF]/20 rounded-3xl p-12">
          <h2 
            className="text-3xl md:text-4xl font-bold mb-6"
            style={{ fontFamily: "'Supersonic Rocketship', Impact, sans-serif" }}
          >
            <span className="text-[#FFE500]">CONTRÁTANOS</span>{" "}
            <span className="text-white">PARA TU EVENTO</span>
          </h2>
          
          <p className="text-xl text-gray-300 mb-8">
            ¿Tienes un evento próximo? Hagamos algo memorable juntos.
          </p>
          
          <Link to="/contacto">
            <Button 
              className="bg-gradient-to-r from-[#E91E8C] to-[#9146FF] hover:opacity-90 text-white text-lg px-10 py-6 rounded-full font-bold"
              data-testid="cta-contact"
            >
              <Mic2 className="mr-2" />
              Solicitar Cotización
            </Button>
          </Link>
          
          <p className="text-gray-500 text-sm mt-6">
            contact@darkarcompany.com
          </p>
        </div>
      </section>

      {/* Footer */}
      <div className="mt-auto">
        <Footer />
      </div>
    </div>
  );
}
